import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { WifiOff, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

// Dir 142 Part B — the up/down pill polls the ULTRA-LIGHT /api/health (instant 200 even while a takeoff pins
// the worker) with a short timeout + a few retries before it EVER renders "Backend Down". A single slow tick
// during a heavy run shows "Reconnecting…", not a hard failure. It uses a BARE axios client (not the shared
// intercepted `api`) so a health probe never itself triggers the interceptor's down/up broadcast.
const POLL_MS = 15000;      // steady liveness cadence
const TIMEOUT_MS = 6000;    // one probe's patience
const DOWN_AFTER = 3;       // consecutive failed light-health probes before we call it truly DOWN

export function BackendStatusBanner() {
  const [status, setStatus] = useState("up"); // up | reconnecting | down
  const failsRef = useRef(0);
  const probe = useRef(axios.create({ baseURL: API, timeout: TIMEOUT_MS }));

  useEffect(() => {
    let alive = true;
    const apply = (ok) => {
      if (!alive) return;
      if (ok) { failsRef.current = 0; setStatus("up"); }
      else {
        failsRef.current += 1;
        setStatus(failsRef.current >= DOWN_AFTER ? "down" : "reconnecting");
      }
    };
    const poll = async () => {
      try {
        const r = await probe.current.get("/health");
        apply(r.status === 200 && r.data && (r.data.live || r.data.status === "ok"));
      } catch {
        apply(false);
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    // Interceptor HINTS (from api.js): a real success clears instantly; a failed request re-probes the light
    // endpoint rather than trusting one timeout — so a slow request during a takeoff can't flip the pill down.
    const onStatus = (e) => {
      if (e.detail && e.detail.up) { failsRef.current = 0; setStatus("up"); }
      else { poll(); }
    };
    window.addEventListener("backend-status", onStatus);
    return () => { alive = false; clearInterval(id); window.removeEventListener("backend-status", onStatus); };
  }, []);

  if (status === "up") return null;
  const down = status === "down";
  return (
    <div
      data-testid={down ? "backend-unreachable-banner" : "backend-reconnecting-banner"}
      className={`fixed top-0 inset-x-0 z-[100] flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium shadow ${down ? "bg-red-600 text-white" : "bg-amber-500 text-zinc-950"}`}
    >
      {down ? <WifiOff className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {down ? "Backend unreachable — retrying automatically" : "Reconnecting…"}
      {down && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
    </div>
  );
}

export default BackendStatusBanner;
